import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationError } from '../../../error-handler/error-handler.service';
import { ContextMenuItem } from '../../models/context-menu-item.model';
import { Menu, MenuItem } from '../../models/menu.model';
import { ContextMenuService } from '../../services/context-menu.service';
import { KeyboardKey, KeystrokeHandler } from '../../utilities/keystroke-handler.utility';
import { MenuSelectItemEvent } from '../menu/menu-select-item.model';
import { MenuComponent } from '../menu/menu.component';

export interface DropdownMenu<T extends MenuItem = MenuItem> extends Menu<T> {
    linkAriaLabel: string;
    linkAriaDescription?: string;
    linkCssClass?: string;
    linkTooltip?: string;
    openCssClass?: string;
    themeCssClass?: string;
}

// This class is a marker directive used in conjunction with DropdownMenuComponent.
// Any HTML content that is placed as a child of a node with this directive will be used as
// the content of the dropdown menu link (this HTML will be enclosed in an <a> tag)
@Directive({
    selector: '[ohpMenuLinkContent]'
})
export class DropdownMenuLinkContentDirective { }

// This class is a directive used in conjunction with DropdownMenuComponent
// Any content that is placed as a child of a node with this directive will be repeated in the dropdown menu as a menu item.
// Usage: *ohpDropdownMenuItemContent='let item = item; let index = index'
// Don't forget the '*' otherwise, it will not work properly!
@Directive({
    selector: '[ohpDropdownMenuItemContent]'
})
export class DropdownMenuItemContentDirective {
    constructor(
        public templateRef: TemplateRef<any>
    ) { }
}

@Component({
    selector: 'ohp-dropdown-menu',
    templateUrl: './dropdown-menu.component.html'
})
export class DropdownMenuComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('dropdownLink') public dropdownLink: ElementRef;

    @ViewChild(MenuComponent) public menu: MenuComponent;

    @ContentChildren(DropdownMenuItemContentDirective) public menuItems: QueryList<DropdownMenuItemContentDirective>;
    public menuItemTemplate: TemplateRef<any>;

    @Input() public menuModel: DropdownMenu;

    @Input() public overridePositioning: boolean;

    // This should only be true when menuModel is of type DropdownMenu<ContextMenuItem>
    @Input() public mobileContextMenu?: boolean;

    @Input() public leftAlignedCard?: boolean = false;

    @Input() public fullWidth?: boolean = false;

    @Input() public toggleDisabled?: boolean = false;

    @Input() public themeEnabled?: Observable<boolean>;

    // TODO: Remove the create-menu's dependency on dropdown-menu when we release create modal.
    @Input() public hideContent?: boolean = false;

    @Input() public multiSelect?: boolean = false;

    @Input() public ariaRole?: string = 'option';

    @Input() public selectedIndex?: number = -1;

    @Output() public readonly toggleMenu: EventEmitter<boolean>;

    @Output() public readonly selectItem: EventEmitter<MenuSelectItemEvent>;

    @Output() public readonly focusLink: EventEmitter<boolean>;

    public opened: boolean;
    public isThemeEnabled: boolean;

    private focusActive: boolean;

    private static readonly expectedKeys = [KeyboardKey.Tab, KeyboardKey.Enter, KeyboardKey.Escape, KeyboardKey.ArrowUp, KeyboardKey.ArrowDown];

    constructor(
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly contextMenuService: ContextMenuService
    ) {
        this.toggleMenu = new EventEmitter<boolean>();
        this.selectItem = new EventEmitter<MenuSelectItemEvent>();
        this.focusLink = new EventEmitter<boolean>();
    }

    public ngOnInit() {
        this.opened = false;
        this.focusActive = false;

        if (this.mobileContextMenu) {
            this.contextMenuService.subscribeToCloseEvents(event => {
                if (event && event.menu && this.menuModel && event.menu.id === this.menuModel.id) {
                    this.onCloseMenu();
                }
            });
        }

        if (this.themeEnabled) {
            this.themeEnabled.subscribe(isThemeEnabled => {
                this.isThemeEnabled = isThemeEnabled;
            });
        }
    }

    public ngOnDestroy() {
        if (!this.opened) {
            return;
        }
        this.toggleMenu.emit(false);
    }

    public ngAfterViewInit() {
        if (this.menuItems.length !== 1) {
            throw new ApplicationError('Must have exactly 1 *ohpDropdownMenuItemContent in a DropdownMenu as a child');
        }

        this.menuItemTemplate = this.menuItems.first.templateRef;

        this.changeDetectorRef.detectChanges();
    }

    public onToggleMenu(open: boolean, event?: MouseEvent | KeyboardEvent): void {
        event?.preventDefault();

        if (this.toggleDisabled && !this.fullWidth) {
            // When create modal is enabled with the app tile create button, every time user clicks create button, it should open the modal.
            // The only way to close the modal is to click the close button on the modal.
            this.opened = true;
            this.toggleMenu.emit(true);
            return;
        }

        if (this.opened === open) {
            return;
        }

        if (!open && this.menu) {
            this.menu.close();
            return;
        }

        if (open && !this.hideContent && this.mobileContextMenu) {
            this.contextMenuService.showMenu({
                menu: this.menuModel as DropdownMenu<ContextMenuItem>,
                position: { left: 0, top: 0 },
                focusFirstItem: this.focusActive
            });
        }

        this.opened = open;
        this.toggleMenu.emit(open);
    }

    public onSelectItem(event: MenuSelectItemEvent): void {
        this.selectItem.emit(event);
    }

    public onCloseMenu(closedByKey?: boolean): void {
        this.opened = false;
        // Returns focus to menu link if menu was activated by keystroke
        if (this.focusActive || closedByKey) {
            this.dropdownLink.nativeElement.focus();
        }
        this.focusActive = false;
        this.toggleMenu.emit(false);
    }

    public handleKey(event: KeyboardEvent) {
        const key = KeystrokeHandler.getKey(event);
        if (!this.opened) {
            if (key === KeyboardKey.Enter) {
                this.focusActive = true;
            }
            return;
        }

        if (DropdownMenuComponent.expectedKeys.indexOf(key) === -1) {
            return;
        }

        event.preventDefault();

        const limit = this.menuModel.items.length - 1;
        switch (key) {
            case KeyboardKey.Tab:
            case KeyboardKey.Enter:
            case KeyboardKey.Escape:
                this.onToggleMenu(false, event);
                break;
            case KeyboardKey.ArrowUp:
                this.focusActive = true;
                this.menu.setActiveIndex(limit, true);
                break;
            case KeyboardKey.ArrowDown:
                this.focusActive = true;
                this.menu.setActiveIndex(0, true);
                break;
        }
    }

    public handleFocus(focused: boolean) {
        this.focusLink.emit(focused);
    }
}
